import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  colorTokens,
  getSpacing,
  getTypography,
  typographyTokens,
  getShadow,
} from "../../../designTokens";
import { userDataApi, updateOwnProfileApi } from "../../../Apis/login";
import {
  getProfilePhotoApi,
  uploadProfilePhotoApi,
} from "../../../Apis/profilePhoto";
import type { UserDataResponse } from "../../../Interfaces/LoginInterface";
import { useAppDispatch } from "../../../redux/hooks";
import { setUserContext } from "../../../redux/slices/UserSlice";
import { identifyUser } from "../../../analytics";
import { toast } from "sonner";
import {
  isPermissionGroupContextError,
  PERMISSION_GROUP_NOT_CONFIGURED_MESSAGE,
} from "../../../utils/teamLeaderAccess";

// Helper to get email string from user
const getEmailFromUser = (
  user: { email?: { value?: string } | string } | null
): string => {
  if (!user?.email) return "";
  const e = user.email;
  return typeof e === "string" ? e : ((e as { value?: string })?.value ?? "");
};

// Helper to get display initial (first letter of name or email)
const getDisplayInitial = (
  fullName: string | undefined,
  email: string
): string => {
  if (fullName?.trim()) {
    return fullName.trim().charAt(0).toUpperCase();
  }
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  return "U";
};

// Helper to strip +91 / 91 from phone number for display in input
const stripPhoneCountryCode = (phone: string | undefined): string => {
  if (!phone?.trim()) return "";
  return phone
    .trim()
    .replace(/^\+91\s*/i, "")
    .replace(/^91\s*/i, "")
    .trim();
};

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

interface ProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  userData: UserDataResponse | null;
  idPrefix?: string;
}

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({
  isOpen,
  onClose,
  userData,
  idPrefix = "profile-drawer",
}) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPhoto, setIsLoadingPhoto] = useState(false);

  const canSave = true;

  // Initialize form from userData
  useEffect(() => {
    if (userData?.user) {
      const u = userData.user;
      const name =
        u.representative?.fullName ?? getEmailFromUser(u)?.split("@")[0] ?? "";
      setFullName(name);
      setEmail(getEmailFromUser(u));
      setPhone(stripPhoneCountryCode(u.phone));
    }
  }, [userData, isOpen]);

  // Fetch profile photo when drawer opens
  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setIsLoadingPhoto(true);
      try {
        const res = await getProfilePhotoApi();
        setProfileImageUrl((prev) => {
          // Don't overwrite if user already selected a new photo (blob URL)
          if (prev?.startsWith("blob:")) return prev;
          return res?.data?.url ?? null;
        });
      } catch {
        setProfileImageUrl((prev) => (prev?.startsWith("blob:") ? prev : null));
      } finally {
        setIsLoadingPhoto(false);
      }
    };
    load();
  }, [isOpen]);

  // Reset photo state when drawer closes (revoke object URL if we created one)
  useEffect(() => {
    if (!isOpen) {
      setProfileImageUrl((prev) => {
        if (prev?.startsWith?.("blob:")) {
          try {
            URL.revokeObjectURL(prev);
          } catch {
            // ignore
          }
        }
        return null;
      });
      setPendingPhotoFile(null);
    }
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (!userData?.user?.id || isSubmitting) return;

    const trimmedFullName = fullName.trim();
    if (!trimmedFullName) {
      toast.error("Full Name is required");
      return;
    }

    setIsSubmitting(true);
    const fileToUpload = pendingPhotoFile;
    try {
      // Upload profile photo first if user selected a new one
      if (fileToUpload) {
        await uploadProfilePhotoApi(fileToUpload);
        setPendingPhotoFile(null);
        if (profileImageUrl) {
          try {
            URL.revokeObjectURL(profileImageUrl);
          } catch {
            // ignore
          }
        }
      }

      // Update profile (self-service: representative only)
      await updateOwnProfileApi({
        representative: { fullName: trimmedFullName },
      });

      // Refetch user context
      let updated;
      try {
        updated = await userDataApi();
      } catch (err) {
        if (isPermissionGroupContextError(err)) {
          toast.error(PERMISSION_GROUP_NOT_CONFIGURED_MESSAGE);
          throw err;
        }
        throw err;
      }
      dispatch(setUserContext(updated));
      // Analytics (W2 remediation, MINOR): the third context-hydration seam —
      // re-identify so tenant/role globals track the refreshed profile
      // (login + session-restore are the other two). Fail-open: a profile
      // save must never break on analytics.
      try {
        identifyUser(updated);
      } catch {
        // fail-open
      }

      // Refetch profile photo if we uploaded one (to get new presigned URL)
      if (fileToUpload) {
        const res = await getProfilePhotoApi();
        if (res?.data?.url) {
          setProfileImageUrl(res.data.url);
        }
      }

      toast.success("Profile updated successfully");
      onClose();
    } catch (err) {
      console.error("Profile update error:", err);
      const msg = (
        err as { response?: { data?: { message?: string; error?: string } } }
      )?.response?.data;
      toast.error(msg?.message ?? msg?.error ?? "Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    userData?.user?.id,
    fullName,
    pendingPhotoFile,
    profileImageUrl,
    isSubmitting,
    dispatch,
    onClose,
  ]);

  const handleEditProfilePic = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleProfileImageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error("Please select a JPEG, PNG, or WebP image");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      setProfileImageUrl((prev) => {
        if (prev) {
          try {
            URL.revokeObjectURL(prev);
          } catch {
            // ignore
          }
        }
        return URL.createObjectURL(file);
      });
      setPendingPhotoFile(file);
      e.target.value = "";
    },
    []
  );

  if (!isOpen) {
    return null;
  }

  const getInputStyle = (fieldId: string): React.CSSProperties => ({
    width: "100%",
    padding: `${getSpacing("12px")} ${getSpacing("16px")}`,
    borderRadius: getSpacing("8px"),
    border: `1px solid ${
      focusedField === fieldId
        ? colorTokens["Primary/Default"]
        : colorTokens["Borders/Light Grey"]
    }`,
    backgroundColor: colorTokens["Background/White"],
    fontFamily: typographyTokens["Body/Medium"].family,
    fontSize: `${typographyTokens["Body/Medium"].size}px`,
    fontWeight: typographyTokens["Body/Medium"].weight,
    lineHeight: 1.5,
    color: colorTokens["Text/Black"],
    outline: "none",
    boxShadow: "none",
    boxSizing: "border-box",
    height: "48px",
    transition: "border-color 0.2s ease",
  });

  const disabledInputStyle: React.CSSProperties = {
    backgroundColor: colorTokens["Background/Greyish"],
    color: colorTokens["Text/Light Grey"],
    cursor: "not-allowed",
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          zIndex: 999,
          animation: "fadeIn 0.3s ease-in-out",
        }}
        onClick={handleBackdropClick}
      />

      {/* Drawer - matches Figma node 7699:124480 */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "628px",
          backgroundColor: colorTokens["Background/White"],
          borderLeft: `1px solid ${colorTokens["Borders/Light Grey"]}`,
          borderRadius: `${getSpacing("16px")} 0 0 ${getSpacing("16px")}`,
          display: "flex",
          flexDirection: "column",
          zIndex: 1000,
          boxShadow: getShadow("Shadow"),
          animation: "slideInFromRight 0.3s ease-in-out",
          overflow: "hidden",
        }}
        id={`${idPrefix}-container`}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: getSpacing("16px"),
            borderBottom: `1px solid ${colorTokens["Borders/Light Grey"]}`,
            minHeight: "64px",
            boxSizing: "border-box",
            flexShrink: 0,
          }}
          id={`${idPrefix}-header`}
        >
          <h2
            style={{
              ...getTypography("Headings/H3"),
              color: colorTokens["Text/Black"],
              margin: 0,
            }}
            id={`${idPrefix}-title`}
          >
            Profile
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              backgroundColor: "transparent",
              border: "none",
              borderRadius: getSpacing("8px"),
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
            }}
            id={`${idPrefix}-close-button`}
            aria-label="Close"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke={colorTokens["Text/Black"]}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: getSpacing("24px"),
            display: "flex",
            flexDirection: "column",
            gap: getSpacing("24px"),
          }}
          id={`${idPrefix}-content`}
        >
          {/* Profile picture - Figma 7699-124489: avatar + Edit link to the right */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: getSpacing("16px"),
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleProfileImageChange}
              style={{ display: "none" }}
              id={`${idPrefix}-file-input`}
            />
            <div
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "50%",
                overflow: "hidden",
                backgroundColor: colorTokens["Background/Bluish"],
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
              id={`${idPrefix}-avatar`}
            >
              {isLoadingPhoto && !profileImageUrl ? (
                <span
                  style={{
                    fontFamily: typographyTokens["Body/Small"].family,
                    fontSize: `${typographyTokens["Body/Small"].size}px`,
                    color: colorTokens["Text/Light Grey"],
                  }}
                >
                  ...
                </span>
              ) : profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <span
                  style={{
                    fontFamily: typographyTokens["Headings/H3"].family,
                    fontSize: "28px",
                    fontWeight: typographyTokens["Headings/H3"].weight,
                    color: colorTokens["Primary/Default"],
                    lineHeight: 1,
                  }}
                >
                  {getDisplayInitial(
                    fullName || userData?.user?.representative?.fullName,
                    email
                  )}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleEditProfilePic}
              style={{
                fontFamily: typographyTokens["Links/Btn Text"].family,
                fontSize: `${typographyTokens["Links/Btn Text"].size}px`,
                fontWeight: typographyTokens["Links/Btn Text"].weight,
                color: colorTokens["Primary/Default"],
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                textAlign: "left",
              }}
              id={`${idPrefix}-edit-pic`}
            >
              Edit Profile pic
            </button>
          </div>

          {/* Form fields - Full Name, Email, Phone */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: getSpacing("16px"),
              alignContent: "start",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: getSpacing("8px"),
                gridColumn: "1 / -1",
              }}
            >
              <label
                htmlFor={`${idPrefix}-fullName`}
                style={{
                  ...getTypography("Body/Small"),
                  color: colorTokens["Text/Black"],
                }}
              >
                Full Name *
              </label>
              <input
                id={`${idPrefix}-fullName`}
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onFocus={() => setFocusedField("fullName")}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...getInputStyle("fullName"),
                  ...(!canSave ? disabledInputStyle : {}),
                }}
                placeholder="Full Name"
                disabled={!canSave}
                readOnly={!canSave}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: getSpacing("8px"),
              }}
            >
              <label
                htmlFor={`${idPrefix}-email`}
                style={{
                  ...getTypography("Body/Small"),
                  color: colorTokens["Text/Black"],
                }}
              >
                Email
              </label>
              <input
                id={`${idPrefix}-email`}
                type="email"
                value={email}
                readOnly
                style={{
                  ...getInputStyle("email"),
                  ...disabledInputStyle,
                }}
                placeholder="Email"
                disabled
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: getSpacing("8px"),
              }}
            >
              <label
                htmlFor={`${idPrefix}-phone`}
                style={{
                  ...getTypography("Body/Small"),
                  color: colorTokens["Text/Black"],
                }}
              >
                Phone Number
              </label>
              <input
                id={`${idPrefix}-phone`}
                type="tel"
                value={phone}
                readOnly
                style={{
                  ...getInputStyle("phone"),
                  ...disabledInputStyle,
                }}
                placeholder="9876543210"
                disabled
              />
            </div>
          </div>
        </div>

        {/* Footer - Cancel outlined, Save primary per Figma */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: getSpacing("12px"),
            padding: getSpacing("16px"),
            borderTop: `1px solid ${colorTokens["Borders/Light Grey"]}`,
            flexShrink: 0,
          }}
          id={`${idPrefix}-footer`}
        >
          <button
            type="button"
            onClick={handleCancel}
            style={{
              padding: `${getSpacing("12px")} ${getSpacing("24px")}`,
              fontFamily: typographyTokens["Links/Btn Text"].family,
              fontSize: `${typographyTokens["Links/Btn Text"].size}px`,
              fontWeight: typographyTokens["Links/Btn Text"].weight,
              color: colorTokens["Primary/Default"],
              backgroundColor: "transparent",
              border: `1px solid ${colorTokens["Primary/Default"]}`,
              borderRadius: getSpacing("8px"),
              cursor: "pointer",
            }}
            id={`${idPrefix}-cancel-button`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || isSubmitting}
            style={{
              padding: `${getSpacing("12px")} ${getSpacing("24px")}`,
              fontFamily: typographyTokens["Links/Btn Text"].family,
              fontSize: `${typographyTokens["Links/Btn Text"].size}px`,
              fontWeight: typographyTokens["Links/Btn Text"].weight,
              color: colorTokens["Background/White"],
              backgroundColor:
                canSave && !isSubmitting
                  ? colorTokens["Primary/Default"]
                  : colorTokens["Text/Light Grey"],
              border: "none",
              borderRadius: getSpacing("8px"),
              cursor: canSave && !isSubmitting ? "pointer" : "not-allowed",
            }}
            id={`${idPrefix}-save-button`}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </>
  );
};

export default ProfileDrawer;
